//components/component/dashboard/auth-screen.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import Script from 'next/script';
import {
  ArrowRight,
  Shield,
  Globe,
  Gauge,
  BarChart,
  Bell,
  Search,
  Wallet,
  Clock,
  LucideIcon,
  ArrowUpCircle,
  ArrowDownCircle,
} from 'lucide-react';

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  delay?: number;
}

interface StatCardProps {
  label: string;
  value: string;
  trend?: number;
}

interface AuthScreenProps {
  handleGuestAccess?: () => void; // Changed to optional
}

const FeatureCard: React.FC<FeatureCardProps> = ({
  icon: Icon,
  title,
  description,
  delay = 0,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700/50 hover:border-blue-500/50 transition-all duration-300 hover:transform hover:scale-105"
  >
    <div className="flex items-start space-x-4">
      <div className="bg-blue-500/10 p-3 rounded-lg">
        <Icon className="w-6 h-6 text-blue-400" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
        <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </motion.div>
);

const StatCard: React.FC<StatCardProps> = ({ label, value, trend }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="bg-slate-800/30 p-4 rounded-lg text-center relative overflow-hidden group hover:bg-slate-800/40 transition-all duration-300"
  >
    <div className="absolute inset-0 bg-gradient-to-t from-blue-500/5 via-transparent to-transparent" />
    <div className="relative z-10">
      <div className="text-sm text-slate-400 mb-1">{label}</div>
      <div className="text-lg font-semibold text-white">{value}</div>
      {trend !== undefined && (
        <div
          className={`text-sm mt-1 flex items-center justify-center gap-1
          ${trend >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
        >
          {trend >= 0 ? (
            <ArrowUpCircle className="w-4 h-4" />
          ) : (
            <ArrowDownCircle className="w-4 h-4" />
          )}
          {Math.abs(trend)}%
        </div>
      )}
    </div>
  </motion.div>
);

const LivePriceDemo: React.FC = () => {
  const [currentPrice, setCurrentPrice] = useState(9637.07);
  const [priceChange, setPriceChange] = useState(-0.8);

  useEffect(() => {
    const interval = setInterval(() => {
      const change = (Math.random() - 0.5) * 20;
      const newPrice = Math.max(8000, Math.min(11000, currentPrice + change));
      const newPriceChange = ((newPrice - currentPrice) / currentPrice) * 100;

      setCurrentPrice(newPrice);
      setPriceChange(newPriceChange);
    }, 3000);

    return () => clearInterval(interval);
  }, [currentPrice]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center justify-between"
    >
      <div>
        <h3 className="text-lg font-medium">Rolex Submariner 16610</h3>
        <div className="flex items-center gap-3">
          <p className="text-blue-400 text-2xl font-bold">
            ${currentPrice.toFixed(2)}
          </p>
          <span
            className={`flex items-center gap-1 ${
              priceChange >= 0 ? 'text-emerald-400' : 'text-rose-400'
            }`}
          >
            {priceChange >= 0 ? (
              <ArrowUpCircle className="w-4 h-4" />
            ) : (
              <ArrowDownCircle className="w-4 h-4" />
            )}
            {Math.abs(priceChange).toFixed(1)}%
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const PreviewChart: React.FC = () => {
  const [data, setData] = useState([40, 70, 45, 90, 65, 85, 75]);

  useEffect(() => {
    const interval = setInterval(() => {
      setData((prev) => {
        const newValue = Math.max(
          30,
          Math.min(100, prev[prev.length - 1] + (Math.random() - 0.5) * 20)
        );
        return [...prev.slice(1), newValue];
      });
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative h-32 w-full overflow-hidden rounded-lg bg-slate-800/30 p-4">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10" />
      <div className="relative h-full flex items-end space-x-2">
        {data.map((height, i) => (
          <motion.div
            key={i}
            initial={{ height: 0 }}
            animate={{ height: `${height}%` }}
            transition={{ duration: 0.5 }}
            className="flex-1 bg-blue-500/40 rounded-t"
          />
        ))}
      </div>
    </div>
  );
};

const MarketInsights: React.FC = () => (
  <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
    <h3 className="text-sm font-medium text-slate-300 mb-3">
      Latest Market Insights
    </h3>
    <div className="space-y-2">
      {[
        { time: '2h ago', text: 'Rolex market shows signs of stabilization' },
        {
          time: '4h ago',
          text: 'Patek Philippe prices surge in Asian markets',
        },
        { time: '6h ago', text: 'New auction record for vintage Submariner' },
      ].map((insight, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 }}
          className="flex items-center gap-2 text-sm"
        >
          <Clock className="w-4 h-4 text-blue-400" />
          <span className="text-slate-400">{insight.time}</span>
          <span className="text-slate-300">{insight.text}</span>
        </motion.div>
      ))}
    </div>
  </div>
);

const AuthScreen: React.FC<AuthScreenProps> = ({
  handleGuestAccess = () => {},
}) => {
  const router = useRouter();

  useEffect(() => {
    const prefetchAuth = async () => {
      await router.prefetch('/auth');
    };
    prefetchAuth();
  }, [router]);

  return (
    <>
      <Script
        id="auth-prefetch"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function() {
              var link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'fetch';
              link.href = '/auth?mode=register';
              document.head.appendChild(link);
            })();
          `,
        }}
      />
      {/* Main container - changed from min-h-screen to h-full with overflow-y-auto */}
      <div className="h-full bg-slate-900 text-white overflow-y-auto">
        {/* Background elements with fixed positioning that won't interfere with scrolling */}
        <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-0 -right-1/4 w-1/2 h-1/2 bg-blue-500/10 blur-[120px] rounded-full animate-pulse" />
          <div className="absolute bottom-0 -left-1/4 w-1/2 h-1/2 bg-purple-500/10 blur-[120px] rounded-full animate-pulse" />
          <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-20" />
        </div>

        {/* Content container with relative positioning and proper padding */}
        <div className="relative container mx-auto px-4 py-12 max-w-6xl min-h-screen">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-blue-500/5 blur-3xl" />
            <div className="flex flex-col items-center justify-center relative">
              <div className="mb-6 relative">
                <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full" />
                <Image
                  src="/watchinspect.svg"
                  width={80}
                  height={80}
                  alt="Watch Inspect Logo"
                  className="relative transform transition-transform duration-200 hover:scale-105"
                  style={{ objectFit: 'contain', height: 'auto' }}
                />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 drop-shadow-[0_0_15px_rgba(59,130,246,0.3)]">
                Welcome to Watch Inspect
                <span className="block text-2xl md:text-3xl mt-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-200 to-blue-400">
                  Your Premium Watch Market Intelligence Platform
                </span>
              </h1>
              <div className="w-full max-w-2xl mx-auto relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 blur-xl -z-10" />
                <p className="text-slate-300 text-lg leading-relaxed relative z-10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  Track prices, analyze market trends, and make informed
                  decisions with real-time data from every major marketplace
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="text-center max-w-2xl mx-auto pb-8" // Added padding bottom
          >
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
              <Button
                onClick={() => router.push('/auth?mode=register')}
                className="w-full md:w-auto px-8 py-6 bg-gradient-to-r from-blue-600 via-blue-700 to-blue-600 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold text-lg flex items-center justify-center gap-2 group shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 relative overflow-hidden"
              >
                <span className="relative z-10">Register for free</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform relative z-10" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              </Button>
              <Button
                onClick={handleGuestAccess}
                variant="outline"
                className="w-full md:w-auto px-8 py-6 bg-gradient-to-r from-transparent to-transparent hover:from-yellow-500/10 hover:to-yellow-500/5 border-2 border-yellow-500/50 text-yellow-400 hover:text-yellow-300 rounded-xl font-semibold text-lg transition-all duration-300 hover:border-yellow-400 backdrop-blur-sm"
              >
                Continue as Guest
              </Button>
            </div>
            <p className="text-slate-400 text-sm mt-6 backdrop-blur-sm bg-slate-800/30 px-4 py-2 rounded-lg inline-block">
              Guest access provides limited features. Sign up for full platform
              access and exclusive benefits.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold">
                  Live Dashboard Preview
                </h2>
                <div className="flex gap-2">
                  <motion.div
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-2 h-2 rounded-full bg-emerald-400"
                  />
                  <span className="text-sm text-slate-400">Live Demo</span>
                </div>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
                <div className="space-y-6">
                  <LivePriceDemo />
                  <PreviewChart />

                  <div className="grid grid-cols-3 gap-4">
                    <StatCard label="Volume" value="142" trend={5.2} />
                    <StatCard label="Listings" value="3.2K" trend={-2.1} />
                    <StatCard label="Sources" value="12" trend={0} />
                  </div>
                </div>
              </div>

              <MarketInsights />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="space-y-6"
            >
              <h2 className="text-2xl font-semibold">Premium Features</h2>
              <div className="space-y-4">
                <FeatureCard
                  icon={Globe}
                  title="Global Market Coverage"
                  description="Access comprehensive data from all major watch marketplaces worldwide. Track prices across different regions and currencies."
                  delay={0.2}
                />
                <FeatureCard
                  icon={BarChart}
                  title="Advanced Analytics"
                  description="Leverage powerful tools for market analysis, price trends, and predictive insights to make data-driven decisions."
                  delay={0.3}
                />
                <FeatureCard
                  icon={Bell}
                  title="Real-time Alerts"
                  description="Get instant notifications for price changes, new listings, and market opportunities that match your criteria."
                  delay={0.4}
                />
                <FeatureCard
                  icon={Wallet}
                  title="Portfolio Management"
                  description="Track and manage your watch collection's value with sophisticated portfolio tools and performance metrics."
                  delay={0.5}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthScreen;
